import { LessonClass, WorkbookItemClass } from "@circleofa/acim-engine"
import { acim } from "@utils/acim"
import { difference, filter, remove } from "lodash-es"

const lessonPreReadMap: Record<number, string> = {
    1: "In",
    51: "Re.1.In",
    81: "Re.2.In",
    111: "Re.3.In",
    141: "Re.4.In",
    171: "Re.5.In",
    181: "181-200.In",
    201: "Re.6.In",
    221: "PtII.In",
    361: "FL",
}

export class ReadingList {
    lessons: Map<number, ReadingItem>
    constructor(readingListStorage: ReadingListStorage) {
        this.lessons = new Map<number, ReadingItem>(
            Array.from({ length: 365 }, (_, i) => [
                i + 1,
                new ReadingItemRegular(i + 1, readingListStorage[i + 1] ?? [], this),
            ]),
        )
        this.lessons.set(0, new ReadingSpecific(1, "In", this))
        this.lessons.set(366, new ReadingSpecific(365, "Ep", this))
    }

    getLessonItem(lesson: string | number) {
        return this.lessons.get(+lesson) ?? null
    }

    get readEpilogue() {
        const _365 = this.lessons.get(365)
        if (!_365) throw new Error("Lesson 365 didn't exist!")
        return _365.isDone("Ep")
    }

    toStorage(): ReadingListStorage {
        const readingListStorage: ReadingListStorage = {}
        this.lessons.forEach((item) => {
            // Ignore lesson 0 and 365 for saving
            if (item instanceof ReadingItemRegular) {
                if (item.hasSomeDone) {
                    readingListStorage[item.lesson] = item.doneList
                }
            }
        })
        return readingListStorage
    }
}

export interface ReadingItem {
    /**
     * The full reading list
     */
    actualList: (string | number)[]
    isDone: (item: string | number) => boolean
    readingList: (string | number)[]
    reminderOnlyReadingList: (string | number)[]
    nonReminderReadingList: (string | number)[]
    readyForReminders: boolean
    isAllDone: boolean
    hasSomeDone: boolean
    doneList: (string | number)[]
    nextUnread: string | number | null
    isWhatIs: boolean

    lesson: number
}

/**
 * For intro and epilogue, we want the intro and ep
 * to actually sync with the 1st and last lessons
 * respectively, so we have a special class that
 * allows us to do this by referencing the parent
 * for state management
 */
class ReadingSpecific implements ReadingItem {
    #lessonOne: ReadingItem
    constructor(
        public readonly lesson: 1 | 365,
        public readonly targetItem: "Ep" | "In",
        public readonly parent: ReadingList,
    ) {
        const lessonOne = this.parent.getLessonItem(lesson)
        if (!lessonOne) throw new Error(`Lesson ${lesson} not set!`)
        this.#lessonOne = lessonOne
    }

    get isIntroDone() {
        return this.#lessonOne.isDone(this.targetItem) ?? false
    }

    get isWhatIs() {
        return false
    }

    get actualList() {
        return [this.targetItem]
    }
    get readingList() {
        return this.actualList
    }
    get reminderOnlyReadingList() {
        return this.actualList
    }
    get nonReminderReadingList() {
        return []
    }
    get readyForReminders() {
        return this.isIntroDone
    }
    get hasSomeDone() {
        return this.isIntroDone
    }
    get isAllDone() {
        return this.isIntroDone
    }

    get nextUnread() {
        if (this.isAllDone) return null
        return this.actualList[0]
    }

    isDone(str: string | number) {
        if (str !== this.actualList[0]) return false
        return this.isIntroDone
    }

    get doneList() {
        return this.isIntroDone ? ["In"] : []
    }
}

/**
 * An item for a reading item
 */
class ReadingItemRegular implements ReadingItem {
    readonly #doneList: (number | string)[] = []
    constructor(
        public readonly lesson: number,
        alreadyDone: (string | number)[],
        private readonly parent: ReadingList,
    ) {
        alreadyDone.forEach((item) => {
            if (!this.#doneList.includes(item)) this.#doneList.push(item)
        })
    }

    get nextUnread() {
        for (const item of this.readingList) {
            if (!this.#doneList.includes(item)) return item
        }
        return null
    }

    get actualList() {
        return getReadingListForLesson(this.lesson)
    }

    isDone(item: string | number) {
        return this.#doneList.includes(item)
    }

    get readingList() {
        return getReadingListForLesson(this.lesson)
    }

    /**
     * These are the lessons that are required to get it good
     */
    get reminderOnlyReadingList() {
        const list = filter(
            this.readingList,
            (item) => !(item.toString().startsWith("WI") || item.toString().startsWith("Ep")),
        )
        return list
    }

    get isWhatIs() {
        return this.readingList.some((item) => item.toString().startsWith("WI"))
    }

    /**
     * These are the lessons that can be read at any point during the day
     * or tomorrow
     */
    get nonReminderReadingList() {
        return difference(getReadingListForLesson(this.lesson), this.reminderOnlyReadingList)
    }

    /**
     * Returns ready for reminders, essentially removes
     * whatever can be read at some point during the day,
     * which is What-Is and the Epilogue
     */
    get readyForReminders() {
        return this.reminderOnlyReadingList.every((item) => this.#doneList.includes(item))
    }

    /**
     * Returns if all reading list is done
     */
    get isAllDone() {
        const list = getReadingListForLesson(this.lesson)
        return list.every((item) => this.#doneList.includes(item))
    }

    get hasSomeDone() {
        return this.#doneList.length > 0
    }

    get doneList() {
        return [...this.#doneList]
    }
}

export type ReadingListStorage = {
    [lesson: number]: (string | number)[]
}

/**
 * Returns the array of reading material for a given lesso number, ignoring the
 * W- prefix.
 * @param lesson number between 1 and 365
 */
export function getReadingListForLesson(lesson: number) {
    const returnList: (string | number)[] = []

    // Get the main intros, reviews, etc
    if (lesson in lessonPreReadMap) returnList.push(lessonPreReadMap[lesson])

    // Check the What-Is sections
    if (lesson >= 221 && lesson <= 360) {
        const whatIs = Math.floor((lesson - 221) / 10) + 1
        returnList.push("WI." + whatIs)
    }

    // Add number
    returnList.push(lesson)

    // If it's the lesson lesson, add it
    if (lesson == 365) returnList.push("Ep")
    return returnList
}

/**
 * Returns the acim lesson object for the given item name
 * @param itemName The shortened path without W-... returned by {@link getReadingListForLesson}
 * @returns
 */
export function getLessonFromItem(itemName: number | string) {
    const found = acim.find("W-" + itemName)
    if (found instanceof WorkbookItemClass) {
        return found
    } else {
        throw new Error("Item name not found or not a workbook item: " + itemName)
    }
}

export function practiceTitle(lesson: WorkbookItemClass) {
    if (lesson instanceof LessonClass) {
        return "Lesson " + (lesson.finalLessons ? "361-365" : lesson.lessonNumber)
    } else {
        return lesson.title?.searchContent
    }
}
